/**
 * Planning AI Service
 * 
 * Specialized service for handling AI operations within the Planning Canvas.
 * Supports both structured generation and conversational AI interactions.
 */

import {
    generateInterviewQuestions
} from '../prompts/planning/interviewQuestions.js';

import {
    generateGuestBriefing
} from '../prompts/planning/guestBriefing.js';

import {
    generateIntroScript
} from '../prompts/planning/introScript.js';

import { MODEL_CONFIGS } from '../config/models.js';

class PlanningAiService {

    /**
     * Generate strategic interview questions based on guest bio and topics.
     * @param {object} context - { guestName, guestBio, topics }
     * @returns {Promise<string>} - Markdown formatted questions
     */
    async generateQuestions(context) {
        try {
            console.log('🤖 Generating interview questions for:', context.guestName);
            const result = await generateInterviewQuestions(context);
            return result;
        } catch (error) {
            console.error('AI Service Error (Questions):', error);
            throw new Error('Failed to generate interview questions. Please try again.');
        }
    }

    /**
     * Create a one-page guest briefing.
     * @param {object} context - { guestName, guestBio }
     * @returns {Promise<string>} - Markdown formatted briefing
     */
    async generateBriefing(context) {
        try {
            console.log('🤖 Generating briefing for:', context.guestName);
            const result = await generateGuestBriefing(context);
            return result;
        } catch (error) {
            console.error('AI Service Error (Briefing):', error);
            throw new Error('Failed to generate guest briefing.');
        }
    }

    /**
     * Write a podcast intro script.
     * @param {object} context - { guestName, guestBio, podcastName }
     * @returns {Promise<string>} - Markdown formatted script
     */
    async generateIntro(context) {
        try {
            console.log('🤖 Generating intro for:', context.guestName);
            const result = await generateIntroScript(context);
            return result;
        } catch (error) {
            console.error('AI Service Error (Intro):', error);
            throw new Error('Failed to generate intro script.');
        }
    }

    /**
     * Send a conversational message to the AI with full context.
     * Supports iterative refinement and follow-up questions.
     * 
     * @param {object} params - { message, context, chatHistory }
     * @param {string} params.message - The user's message
     * @param {object} params.context - Guest context { guestName, guestBio, topics, podcastName }
     * @param {Array} params.chatHistory - Previous messages in the conversation
     * @returns {Promise<object>} - { response, followUpQuestions }
     */
    async chat({ message, context, chatHistory = [] }) {
        try {
            console.log('🤖 Processing chat message for:', context.guestName);

            // Build the system prompt with guest context
            const systemPrompt = this._buildSystemPrompt(context);

            // Build messages array for the API
            const messages = [
                { role: 'system', content: systemPrompt },
                ...chatHistory.map(msg => ({
                    role: msg.role,
                    content: msg.content
                })),
                { role: 'user', content: message }
            ];

            // Call the API with conversation history
            const response = await this._callChatAPI(messages);

            // Parse the response to extract follow-up questions if present
            const { content, followUpQuestions } = this._parseResponse(response);

            return {
                response: content,
                followUpQuestions
            };
        } catch (error) {
            console.error('AI Service Error (Chat):', error);
            throw new Error('Failed to process your request. Please try again.');
        }
    }

    /**
     * Build the system prompt with guest context.
     */
    _buildSystemPrompt(context) {
        const guestInfo = [];

        if (context.guestName) {
            guestInfo.push(`Guest Name: ${context.guestName}`);
        }
        if (context.guestBio) {
            guestInfo.push(`Guest Bio: ${context.guestBio}`);
        }
        if (context.topics) {
            guestInfo.push(`Topics: ${Array.isArray(context.topics) ? context.topics.join(', ') : context.topics}`);
        }
        if (context.podcastName) {
            guestInfo.push(`Podcast: ${context.podcastName}`);
        }

        return `You are a friendly, conversational AI assistant for podcast planning. Think of yourself as a knowledgeable colleague who's here to help, not a formal assistant.

${guestInfo.length > 0 ? 'CURRENT GUEST CONTEXT:\n' + guestInfo.join('\n') + '\n' : ''}

PERSONALITY & TONE:
- Be warm, casual, and conversational - like chatting with a smart friend
- Use natural language, not overly formal or robotic responses
- It's okay to be brief for simple questions - don't over-explain
- Show enthusiasm about interesting topics and guests
- Use "I" and "you" naturally - we're having a conversation

HOW TO HANDLE DIFFERENT QUERY TYPES:

1. SIMPLE QUESTIONS (e.g., "Who is Nick Turner?", "What was his occupation?"):
   - Answer directly and conversationally based on the guest context
   - Be concise - a few sentences is fine
   - Don't generate formal reports or markdown sections unless asked
   - Example: "Nick Turner is a serial entrepreneur who founded three tech startups. He's currently the CEO of DataFlow Inc."

2. CONTENT GENERATION (e.g., "Create interview questions", "Write a briefing"):
   - Provide well-structured, useful content
   - Use markdown formatting for clarity
   - After generating, offer 2-3 follow-up questions to refine

3. VAGUE REQUESTS (e.g., "Help me with this guest"):
   - Ask 1-2 clarifying questions to understand what they need
   - Offer a few options: "I can help with questions, a briefing, or talking points - what would be most useful?"

4. CONVERSATIONAL FOLLOW-UPS:
   - Remember what we discussed
   - Build on previous responses naturally
   - If they ask for changes, make them without re-explaining everything

FOLLOW-UP QUESTIONS:
After providing substantial content (questions, briefings, scripts), suggest 2-3 relevant follow-ups using this format:
[FOLLOW_UP_QUESTIONS]
1. First question?
2. Second question?
[/FOLLOW_UP_QUESTIONS]

IMPORTANT: Only use follow-up questions after generating actual content. For simple Q&A, just respond naturally without them.`;
    }

    /**
     * Call the chat API with messages.
     */
    async _callChatAPI(messages) {
        const apiUrl = 'https://api.openai.com/v1/chat/completions';
        const apiKey = process.env.REACT_APP_OPENAI_API_KEY || 'your-openai-api-key-here';

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${apiKey}`
            },
            body: JSON.stringify({
                model: MODEL_CONFIGS.PLANNING.model,
                messages: messages,
                temperature: 0.7,
                max_tokens: 2000
            })
        });

        if (!response.ok) {
            const errorBody = await response.text();
            throw new Error(`API request failed: ${errorBody}`);
        }

        const result = await response.json();

        if (result.choices && result.choices.length > 0 && result.choices[0].message) {
            return result.choices[0].message.content;
        }

        throw new Error('No content generated');
    }

    /**
     * Parse the response to extract content and follow-up questions.
     */
    _parseResponse(response) {
        const followUpRegex = /\[FOLLOW_UP_QUESTIONS\]([\s\S]*?)\[\/FOLLOW_UP_QUESTIONS\]/;
        const match = response.match(followUpRegex);

        let content = response;
        let followUpQuestions = [];

        if (match) {
            // Remove the follow-up section from the content
            content = response.replace(followUpRegex, '').trim();

            // Parse the questions
            const questionsText = match[1].trim();
            const questionLines = questionsText.split('\n').filter(line => line.trim());

            followUpQuestions = questionLines.map(line => {
                // Remove numbering (1., 2., etc.) and trim
                return line.replace(/^\d+\.\s*/, '').trim();
            }).filter(q => q.length > 0);
        }

        return { content, followUpQuestions };
    }
}

const planningAiService = new PlanningAiService();
export default planningAiService;
